import {
    Features,
    useFeatureFlagValue,
} from '@experiences/feature-flags';
import { useApolloTheme } from '@experiences/theme';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import { PortalCustomIcon } from '@uipath/portal-shell-react';
import clsx from 'clsx';
import type { FC } from 'react';
import React from 'react';
import { useIntl } from 'react-intl';

import useCheckLicense from '../../../../common/hooks/useCheckLicense';
import EditNavIconFormComponent from './EditNavIconFormComponent';

const useStyles = makeStyles(theme =>
    createStyles({
        description: {
            lineHeight: '20px',
            color: theme.palette.semantic.colorForegroundDeEmp,
        },
        grid: {
            marginTop: '16px',
            marginBottom: '16px',
            width: '100%',
        },
        icon: {
            width: '24px',
            height: '24px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginRight: '8px',
            '& svg': { fill: theme.palette.semantic.colorIconDefault },
        },
        title: {
            fontWeight: 600,
            fontSize: '14px',
            lineHeight: '20px',
            color: theme.palette.semantic.colorForeground,
            marginBottom: '4px',
        },
        tableHeaderCell: {
            backgroundColor: theme.palette.semantic.colorBackgroundSecondary,
            borderTop: 0,
            padding: '10px 0px 10px 20px',
            height: '40px',
            minWidth: '35%',
        },
        tableHeaderCellAction: {
            padding: '10px 20px',
            height: '40px',
            minWidth: '30%',
            display: 'flex',
            justifyContent: 'center',
        },
        tableHeaderText: { color: theme.palette.semantic.colorForeground },
    }),
);

const NavCustomizationForm: FC = () => {
    const EnableStudioToggleForEnterpriseAndPro = useFeatureFlagValue(Features.EnableStudioToggleForEnterpriseAndPro.name);
    const EnableMarketplaceToggleForEnterprise = useFeatureFlagValue(Features.EnableMarketplaceToggleForEnterprise.name);
    const HideAppsToggle = useFeatureFlagValue(Features.HideAppsToggle.name);
    const HideMarketplaceToggle = useFeatureFlagValue(Features.HideMarketplaceToggle.name);
    const HideStudioToggle = useFeatureFlagValue(Features.HideStudioToggle.name);

    const classes = useStyles();
    const { formatMessage: translate } = useIntl();
    const { theme } = useApolloTheme();
    const {
        isAccountLicenseEnterpriseOrPro, isUserEnterprise,
    } = useCheckLicense();

    return (
        <>
            <Typography className={classes.title}>
                {translate({ id: 'CLIENT_UI_CUSTOMIZATION' })}
            </Typography>
            <Typography className={classes.description}>
                {translate({ id: 'CLIENT_NAVIGATION_CUSTOMIZATION_DESCRIPTION' })}
            </Typography>
            <TableContainer className={classes.grid}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell className={clsx(classes.tableHeaderCell)}>
                                <Typography className={classes.tableHeaderText}>
                                    {translate({ id: 'CLIENT_UI_ELEMENT' })}
                                </Typography>
                            </TableCell>
                            <TableCell className={clsx(classes.tableHeaderCell)}>
                                <Typography className={classes.tableHeaderText}>
                                    {translate({ id: 'CLIENT_EXPERIENCE' })}
                                </Typography>
                            </TableCell>
                            <TableCell
                                className={clsx(classes.tableHeaderCell, classes.tableHeaderCellAction)}
                                align='center'>
                                <Typography className={classes.tableHeaderText}>
                                    {translate({ id: 'CLIENT_HIDE_SHOW' })}
                                </Typography>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {!HideAppsToggle && <EditNavIconFormComponent
                            id="apps"
                            nameTextId="CLIENT_APPS"
                            titleTextId="CLIENT_NAV_BAR_EDIT_CONFIRMATION_TITLE"
                            descriptionTextId="CLIENT_NAV_BAR_EDIT_CONFIRMATION_DESCRIPTION"
                            tooltipTextId="CLIENT_TOGGLE_APPS_ICON_TOOLTIP"
                            uiLocation={translate({ id: 'CLIENT_NAVIGATION_BAR_EXPERIENCE' })}
                            icon={
                                <div className={classes.icon}>
                                    <PortalCustomIcon
                                        color={theme.palette.semantic.colorIconDefault}
                                        name="apps" />
                                </div>
                            } />}
                        {
                            !HideMarketplaceToggle && (!isUserEnterprise || EnableMarketplaceToggleForEnterprise) &&
                            <EditNavIconFormComponent
                                id="marketplace"
                                nameTextId="CLIENT_MARKETPLACE"
                                titleTextId="CLIENT_NAV_BAR_EDIT_CONFIRMATION_TITLE"
                                descriptionTextId="CLIENT_NAV_BAR_EDIT_CONFIRMATION_DESCRIPTION"
                                tooltipTextId="CLIENT_TOGGLE_MARKETPLACE_ICON_TOOLTIP"
                                uiLocation={translate({ id: 'CLIENT_NAVIGATION_BAR_EXPERIENCE' })}
                                icon={
                                    <div className={classes.icon}>
                                        <PortalCustomIcon
                                            color={theme.palette.semantic.colorIconDefault}
                                            name="marketplace" />
                                    </div>
                                } />
                        }
                        {
                            !HideStudioToggle && (!isAccountLicenseEnterpriseOrPro() || EnableStudioToggleForEnterpriseAndPro) &&
                            <EditNavIconFormComponent
                                id="studio"
                                nameTextId="CLIENT_STUDIO"
                                titleTextId="CLIENT_NAV_BAR_EDIT_CONFIRMATION_TITLE"
                                descriptionTextId="CLIENT_NAV_BAR_EDIT_CONFIRMATION_DESCRIPTION"
                                tooltipTextId="CLIENT_TOGGLE_STUDIO_ICON_TOOLTIP"
                                uiLocation={translate({ id: 'CLIENT_NAVIGATION_BAR_EXPERIENCE' })}
                                icon={
                                    <div className={classes.icon}>
                                        <PortalCustomIcon
                                            color={theme.palette.semantic.colorIconDefault}
                                            name="studio" />
                                    </div>
                                } />
                        }
                        <EditNavIconFormComponent
                            id="resourceCenter"
                            nameTextId="CLIENT_DOWNLOADS"
                            titleTextId="CLIENT_NAV_PAGE_EDIT_CONFIRMATION_TITLE"
                            descriptionTextId="CLIENT_RESOURCE_MENU_WIDGET_EDIT_CONFIRMATION_DESCRIPTION"
                            tooltipTextId="CLIENT_TOGGLE_DOWNLOADS_ICON_TOOLTIP"
                            uiLocation={translate({ id: 'CLIENT_RESOURCE_MENU_WIDGETS_EXPERIENCE' })}
                            icon={
                                <div className={classes.icon}>
                                    <DownloadOutlinedIcon />
                                </div>
                            } />
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};

export default NavCustomizationForm;
